import type { ComponentType } from 'react';
import {
  createApiFactory,
  createComponentExtension,
  createPlugin,
  createRoutableExtension,
  discoveryApiRef,
  fetchApiRef,
} from '@backstage/core-plugin-api';

import { rootRouteRef } from './routes';
import { EnvironmentClient, environmentApiRef } from './api/EnvironmentApi';

export const environmentPlugin = createPlugin({
  id: 'environment',
  routes: { root: rootRouteRef },
  apis: [
    createApiFactory({
      api: environmentApiRef,
      deps: { discoveryApi: discoveryApiRef, fetchApi: fetchApiRef },
      factory: ({ discoveryApi, fetchApi }) =>
        new EnvironmentClient({ discoveryApi, fetchApi }),
    }),
  ],
});

/**
 * The environment inventory, mounted at /environments, and one page per
 * environment beneath it.
 */
export const EnvironmentPage = environmentPlugin.provide(
  createRoutableExtension({
    name: 'EnvironmentPage',
    component: () =>
      import('./components/EnvironmentRoutes').then(m => m.EnvironmentRoutes),
    mountPoint: rootRouteRef,
  }),
);

/**
 * A tab for a catalog entity page.
 *
 * The tabs know nothing about the catalog: they read which environment they
 * show from a context, which the environment page provides itself. On an
 * entity page this wrapper provides it from the entity instead.
 */
const entityTab = (name: string, load: () => Promise<ComponentType>) =>
  environmentPlugin.provide(
    createComponentExtension({
      name,
      component: {
        lazy: async () => {
          const [{ withEntity }, Tab] = await Promise.all([
            import('./components/tabs/withEntity'),
            load(),
          ]);
          return withEntity(Tab);
        },
      },
    }),
  );

export const EntityEnvironmentOverviewContent = entityTab(
  'EntityEnvironmentOverviewContent',
  () => import('./components/tabs/OverviewTab').then(m => m.OverviewTab),
);

export const EntityEnvironmentTerminalContent = entityTab(
  'EntityEnvironmentTerminalContent',
  () => import('./components/tabs/TerminalTab').then(m => m.TerminalTab),
);

export const EntityEnvironmentResourcesContent = entityTab(
  'EntityEnvironmentResourcesContent',
  () => import('./components/tabs/ResourcesTab').then(m => m.ResourcesTab),
);

export const EntityEnvironmentGraphContent = entityTab(
  'EntityEnvironmentGraphContent',
  () => import('./components/tabs/GraphTab').then(m => m.GraphTab),
);

export const EntityEnvironmentStatsContent = entityTab(
  'EntityEnvironmentStatsContent',
  () => import('./components/tabs/StatsTab').then(m => m.StatsTab),
);

export const EntityEnvironmentLogsContent = entityTab(
  'EntityEnvironmentLogsContent',
  () => import('./components/tabs/LogsTab').then(m => m.LogsTab),
);
